import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, ArrowUpRightIcon, ChevronDownIcon, CheckCircle2Icon, HeartIcon } from 'lucide-react';
import { SISTER_ORGANIZATIONS } from '../data/sister-organizations';
const NAVY = '#0b1f3f';
const BLUE = '#1565C0';
const RED = '#cf142b';
const EASE = [0.23, 1, 0.32, 1] as const;
interface NetworkOrganizationsProps {
  isEs: boolean;
}

/**
 * Expandable spotlight list for organizations that have supplied a logo.
 * Collapsed, each row shows only the logo, name, and location; expanding
 * reveals the mission, leadership, and where the organization works.
 */
export function NetworkOrganizations({
  isEs
}: NetworkOrganizationsProps) {
  const orgs = SISTER_ORGANIZATIONS.filter(o => o.logo);
  const [open, setOpen] = useState<string | null>(orgs[0]?.name ?? null);
  if (orgs.length === 0) return null;
  return <ul className="flex flex-col gap-4" data-id="element-2936" data-mp="3cn1hy4sa93a1">
      {orgs.map(org => {
      const isOpen = open === org.name;
      const panelId = `org-panel-${org.name.replace(/\s+/g, '-')}`;
      return <li key={org.name} className="rounded-2xl border bg-white overflow-hidden" style={{
        borderColor: 'rgba(11,31,63,0.14)'
      }} data-id="element-2937" data-mp="25usqz29yxoep">
            <div className="flex items-center gap-1.5 px-5 sm:px-6 py-2 border-b" style={{
          backgroundColor: 'rgba(22,163,74,0.07)',
          borderColor: 'rgba(11,31,63,0.1)'
        }} data-id="element-2938" data-mp="2ew0gauuibewn">
              <CheckCircle2Icon className="w-3.5 h-3.5 flex-shrink-0" style={{
            color: '#16a34a'
          }} aria-hidden="true" data-id="element-2939" data-mp="1m7uus5vmplvr" />
              <span className="font-nunito text-[10px] font-bold uppercase tracking-[0.18em]" style={{
            color: '#15803d'
          }} data-id="element-2940" data-mp="1vyz6mwq0cqmd">
                {isEs ? 'Organización 501(c)(3) Registrada' : 'Registered 501(c)(3)'}
              </span>
            </div>

            <button type="button" onClick={() => setOpen(isOpen ? null : org.name)} aria-expanded={isOpen} aria-controls={panelId} className="w-full flex items-center gap-5 p-5 sm:p-6 text-left transition-colors duration-200 hover:bg-[#f5f8fc]" data-id="element-2941" data-mp="26po8w9p0mss5">
              <img src={org.logo} alt={`${org.name} logo`} className="w-20 h-20 sm:w-24 sm:h-24 flex-shrink-0 rounded-xl object-contain bg-white" data-id="element-2942" data-mp="1nsn0sg060cjz" />
              <div className="min-w-0 flex-1" data-id="element-2943" data-mp="1wu39mjbc06m3">
                <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-1.5" style={{
              color: BLUE
            }} data-id="element-2944" data-mp="1wrqknm75ovzj">
                  {isEs ? org.focusEs : org.focus}
                </p>
                <h3 className="font-bebas text-2xl sm:text-3xl tracking-[0.03em] leading-tight mb-1.5" style={{
              color: NAVY
            }} data-id="element-2945" data-mp="33aorjq8luzex">
                  {org.name}
                </h3>
                <p className="inline-flex items-center gap-1.5 font-nunito text-xs uppercase tracking-[0.12em]" style={{
              color: 'rgba(11,31,63,0.55)'
            }} data-id="element-2946" data-mp="3fu2hf9i0rsu7">
                  <MapPinIcon className="w-3 h-3 flex-shrink-0" style={{
                color: RED
              }} data-id="element-2947" data-mp="36s31ylmnkwtd" />
                  {org.location}
                  {org.serviceArea ? ` · ${isEs ? org.serviceAreaEs : org.serviceArea}` : ''}
                </p>
              </div>
              <ChevronDownIcon className="w-5 h-5 flex-shrink-0 transition-transform duration-200" style={{
            color: NAVY,
            transform: isOpen ? 'rotate(180deg)' : 'none'
          }} aria-hidden="true" data-id="element-2948" data-mp="3ppx7gpokoq70" />
            </button>

            <AnimatePresence initial={false} data-id="element-2949" data-mp="2tlyjn5nllxec">
              {isOpen ? <motion.div id={panelId} initial={{
            height: 0,
            opacity: 0
          }} animate={{
            height: 'auto',
            opacity: 1
          }} exit={{
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.26,
            ease: EASE
          }} className="overflow-hidden" data-id="element-2950" data-mp="2pjbuxdm84sld">
                  <div className="border-t" style={{
              borderColor: 'rgba(11,31,63,0.12)'
            }} data-id="element-2951" data-mp="3db8tw44krq52">
                    <div className="grid md:grid-cols-[minmax(0,1.55fr)_minmax(0,1fr)]" data-id="element-2952" data-mp="33il3br46uies">
                      {/* Mission + where they work */}
                      <div className="px-5 sm:px-7 py-7" data-id="element-2953" data-mp="2yi5nmswfoytn">
                        <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-3" style={{
                    color: 'rgba(11,31,63,0.4)'
                  }} data-id="element-2954" data-mp="3w0p81u96nh3y">
                          {isEs ? 'Misión' : 'Mission'}
                        </p>
                        <p className="font-nunito text-[17px] leading-[1.65] mb-7" style={{
                    color: 'rgba(11,31,63,0.82)'
                  }} data-id="element-2955" data-mp="7ek7twxhpqoy">
                          {isEs ? org.missionEs || org.descriptionEs : org.mission || org.description}
                        </p>

                        {org.partnerSites && org.partnerSites.length > 0 ? <div className="pt-6 border-t" style={{
                    borderColor: 'rgba(11,31,63,0.1)'
                  }} data-id="element-2956" data-mp="v2tokkfujo98">
                            <p className="font-nunito text-[10px] font-bold uppercase tracking-[0.22em] mb-3" style={{
                      color: 'rgba(11,31,63,0.4)'
                    }} data-id="element-2957" data-mp="151sx830m3jw8">
                              {isEs ? 'Su trabajo en Honduras' : 'Their work in Honduras'}
                            </p>
                            <ul className="flex flex-col gap-2" data-id="element-2958" data-mp="3ex44f3myq556">
                              {org.partnerSites.map(site => <li key={site.name} className="inline-flex items-center gap-2 font-nunito text-sm font-bold" style={{
                        color: NAVY
                      }} data-id="element-2959" data-mp="3a42nyr73xxjh">
                                  <MapPinIcon className="w-3.5 h-3.5 flex-shrink-0" style={{
                          color: RED
                        }} data-id="element-2960" data-mp="1p0pekdgala8" />
                                  {site.location}
                                </li>)}
                            </ul>
                          </div> : null}
                      </div>

                      {/* Leadership panel */}
                      {org.director ? <div className="px-5 sm:px-7 py-7 border-t md:border-t-0 md:border-l flex flex-col" style={{
                  borderColor: 'rgba(11,31,63,0.1)',
                  backgroundColor: '#f6f9fc'
                }} data-id="element-2961" data-mp="1fllkeblsn38p">
                          <div className="flex items-center gap-5" data-id="element-2962" data-mp="1anabq478iys2">
                            {/* Constant portrait frame — every organization's
                                leadership photo renders at the same size and
                                crop regardless of the source image. */}
                            <div className="w-44 h-56 sm:w-52 sm:h-64 flex-shrink-0 rounded-lg overflow-hidden bg-white p-2" style={{
                      border: '1px solid rgba(11,31,63,0.16)',
                      boxShadow: '0 1px 3px rgba(11,31,63,0.08)'
                    }} data-id="element-2963" data-mp="2r225olk14e3h">
                              <img src={org.director.photo} alt={org.director.name || `${org.name} ${org.director.title}`} className="w-full h-full rounded object-cover object-top" data-id="element-2964" data-mp="3uswp8ne8nirl" />
                            </div>
                            <div className="min-w-0" data-id="element-2965" data-mp="37u081317tkv3">
                              {org.director.name ? <p className="font-bebas text-2xl tracking-[0.03em] leading-tight" style={{
                        color: NAVY
                      }} data-id="element-2966" data-mp="1a3yv2q5lgaae">
                                  {org.director.name}
                                </p> : null}
                              <p className="font-nunito text-[11px] font-bold uppercase tracking-[0.16em] mt-1" style={{
                        color: BLUE
                      }} data-id="element-2967" data-mp="2sxijexps0v60">
                                {isEs ? org.director.titleEs : org.director.title}
                              </p>
                              {org.director.linkedin ? <a href={org.director.linkedin} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 mt-2.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70" style={{
                        color: 'rgba(11,31,63,0.55)'
                      }} data-id="element-2968" data-mp="2e116w35upahv">
                                  LinkedIn
                                  <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-2969" data-mp="25l6yx1ercetv" />
                                </a> : null}
                            </div>
                          </div>
                        </div> : null}
                    </div>

                    {/* Actions — matched buttons across the full width */}
                    {org.donateUrl || org.url ? <div className="px-5 sm:px-7 py-5 border-t flex flex-col sm:flex-row gap-3" style={{
                borderColor: 'rgba(11,31,63,0.1)'
              }} data-id="element-2970" data-mp="1rn8zr390coyy">
                        {org.url ? <a href={org.url} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:brightness-110" style={{
                  backgroundColor: RED
                }} data-id="element-2971" data-mp="332nr9rig6omo">
                            {isEs ? 'Visitar sitio web' : 'Visit Website'}
                            <ArrowUpRightIcon className="w-3.5 h-3.5" data-id="element-2972" data-mp="crh3f8jrom74" />
                          </a> : null}
                        {org.donateUrl ? <a href={org.donateUrl} target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg px-5 py-3 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] text-white transition-colors duration-200 hover:brightness-110" style={{
                  backgroundColor: '#4FA8E8'
                }} data-id="element-2973" data-mp="3p7wesnpjayh">
                            <HeartIcon className="w-3.5 h-3.5" data-id="element-2974" data-mp="2h1awwa7bjwla" />
                            {isEs ? 'Donar / Participar' : 'Donate / Get Involved'}
                          </a> : null}
                      </div> : null}
                  </div>
                </motion.div> : null}
            </AnimatePresence>
          </li>;
    })}
    </ul>;
}